import { useCallback, useContext, useRef, useSyncExternalStore } from "react";

import AnchorNavContext from "../../components/AnchorNavigation/AnchorNavContext";

const initialState = {
    visibleSections: [],
    scrollY: 0,
    scrollDirection: 0,
    active: ""
};

export function useAnchorNavStoreInit() {
    const store = useRef(initialState);

    const get = useCallback(() => store.current, []);

    const set = useCallback((value) => {
        store.current = { ...store.current, ...value };
        subscribers.current.forEach((callback) => callback());
    }, []);

    const subscribers = useRef(new Set());

    const subscribe = useCallback((callback) => {
        subscribers.current.add(callback);
        return () => subscribers.current.delete(callback);
    });

    const addVisibleSection = useCallback((sectionId) => {
        if (!sectionId) {
            console.error(
                "Section could not be added since sectionId is not defined!"
            );
            return;
        }

        if (!store.current.visibleSections.some((id) => id === sectionId)) {
            set({
                visibleSections: [...store.current.visibleSections, sectionId]
            });
        }
    }, []);

    const removeVisibleSection = useCallback((sectionId) => {
        if (!sectionId) {
            console.error(
                "Section could not be removed since sectionId is not defined!"
            );
            return;
        }

        if (store.current.visibleSections.some((id) => id === sectionId)) {
            set({
                visibleSections: [
                    ...store.current.visibleSections.filter(
                        (id) => id !== sectionId
                    )
                ]
            });
        }
    }, []);

    const onVisibilityChange = useCallback((isVisible, sectionId) => {
        const newScroll = window.scrollY;

        if (
            newScroll > store.current.scrollY &&
            store.current.scrollDirection !== 1
        ) {
            set({ scrollDirection: 1 });
        }

        if (
            newScroll < store.current.scrollY &&
            store.current.scrollDirection !== -1
        ) {
            set({ scrollDirection: -1 });
        }

        set({ srollY: window.scrollY });

        if (isVisible) {
            addVisibleSection(sectionId);
        } else {
            removeVisibleSection(sectionId);
        }

        let newActive = "";

        if (
            store.current.visibleSections.length === 1 ||
            store.current.scrollDirection > 0
        ) {
            newActive = store.current.visibleSections[0];
        } else if (store.current.scrollDirection < 0) {
            newActive =
                store.current.visibleSections[
                    store.current.visibleSections.length - 1
                ];
        }

        set({ active: newActive });
    }, []);

    return { get, set, subscribe, onVisibilityChange };
}

export function useAnchorNavStore(selector) {
    const store = useContext(AnchorNavContext);
    if (!store) {
        throw new Error("Store not found");
    }

    const state = useSyncExternalStore(
        store.subscribe,
        () => selector(store.get()),
        () => selector(initialState)
    );

    return [state, store.set];
}
